/**
 * Markeroo Design System - Button Variants
 *
 * Official button styles from the Figma design system.
 * See docs/design/buttons.md for full specifications.
 *
 * Usage:
 *   import { buttonVariants, pillVariants } from "@markeroo/theme/buttons";
 *   <button className={buttonVariants.primary}>Click me</button>
 */

/**
 * Base styles shared by all buttons.
 * - 8px border radius (rounded-lg)
 * - Focus ring for accessibility
 * - Disabled state styling
 * - Transition for hover effects
 */
const buttonBase =
  "inline-flex items-center justify-center rounded-lg font-semibold transition cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Button variants from the Figma design system.
 *
 * | Variant   | Background        | Text Color    | Use Case                              |
 * |-----------|-------------------|---------------|---------------------------------------|
 * | primary   | Brand Blue (#2D66B3) | White      | Main CTAs, primary actions            |
 * | secondary | White + Blue Border | Brand Blue | Secondary actions, outlined style     |
 * | tertiary  | Gray (#EFEFEF)    | Dark (#191F4C)| Cancel, Close, less prominent actions |
 * | ghost     | Transparent       | Dark (#191F4C)| Minimal actions like "Reset"          |
 * | danger    | Red (#DC2626)     | White         | Destructive actions                   |
 * | ghostDanger | Transparent     | Red (#DC2626) | Less prominent destructive actions    |
 * | highlight | Orange (#FF996E)  | White         | Marketing CTAs                        |
 */
export const buttonVariants = {
  /**
   * Primary Button - Main call-to-action
   * Background: #2D66B3 (Brand Blue)
   * Text: White
   * Height: 38px | Padding: 16px × 10px | Font: 14px Semibold
   */
  primary: `${buttonBase} bg-brand-600 text-white hover:bg-brand-700 focus-visible:outline-brand-600 h-[38px] px-4 py-2.5 text-label-md`,

  /**
   * Secondary Button - Outlined style for secondary actions
   * Background: White
   * Border: 1.5px #2D66B3
   * Text: #2D66B3 (Brand Blue)
   * Height: 38px | Padding: 16px × 10px | Font: 14px Semibold
   */
  secondary: `${buttonBase} bg-white border-[1.5px] border-brand-600 text-brand-600 hover:bg-brand-50 focus-visible:outline-brand-600 h-[38px] px-4 py-2.5 text-label-md`,

  /**
   * Tertiary Button - For less prominent actions like Close/Cancel
   * Background: #EFEFEF (Gray)
   * Text: #191F4C (Dark Blue)
   * Padding: 12px × 10px | Font: 15px Medium
   */
  tertiary: `${buttonBase} bg-surface-button-secondary text-brand-950 hover:bg-surface-button-secondary-hover focus-visible:outline-brand-600 px-3 py-2.5 text-body font-medium`,

  /**
   * Ghost Button - Minimal/text-only style
   * Background: Transparent (White)
   * Text: #191F4C (Dark Blue)
   * Padding: 12px × 10px | Font: 15px Medium
   */
  ghost: `${buttonBase} bg-transparent text-brand-950 hover:bg-surface-hover focus-visible:outline-brand-600 px-3 py-2.5 text-body font-medium`,

  /**
   * Danger Button - For destructive actions
   * Background: #DC2626 (Red)
   * Text: White
   */
  danger: `${buttonBase} bg-semantic-error text-white hover:bg-semantic-error-hover focus-visible:outline-semantic-error h-[38px] px-4 py-2.5 text-label-md`,

  /**
   * Ghost Danger Button - Less prominent destructive actions
   * Background: Transparent
   * Text: #DC2626 (Red)
   */
  ghostDanger: `${buttonBase} bg-transparent text-semantic-error hover:bg-semantic-error-bg focus-visible:outline-semantic-error px-3 py-2.5 text-body font-medium`,

  /**
   * Highlight Button - Marketing CTAs
   * Background: #FF996E (Orange)
   * Text: White
   */
  highlight: `${buttonBase} bg-highlight text-white hover:bg-highlight-hover focus-visible:outline-highlight h-[38px] px-4 py-2.5 text-label-md`,

  /**
   * Warning Button - For warning actions
   * Background: Amber
   * Text: White
   */
  warning: `${buttonBase} bg-semantic-warning text-white hover:opacity-90 focus-visible:outline-semantic-warning h-[38px] px-4 py-2.5 text-label-md`,

  /**
   * Link Button - Outlined with lighter border for navigation-like actions
   * Background: Transparent
   * Border: Brand 100
   * Text: Brand Blue
   */
  link: `${buttonBase} border border-brand-100 bg-transparent text-brand-600 hover:bg-brand-50 focus-visible:outline-brand-600 px-4 py-2 text-label-md`,
} as const;

/**
 * Pill/Tag variants for badges and criteria items.
 * Uses rounded-full for pill shape.
 *
 * - primary: Default for criteria tags
 * - Semantic variants (success, warning, error, info, processing): For status badges
 */
const pillBase =
  "inline-flex items-center rounded-full font-medium transition";

export const pillVariants = {
  /**
   * Default Pill - Standard criteria tags (subtle, fades into background)
   * Background: #E6EDF8 (Blue 100)
   * Text: #1F375B (Blue 950)
   * Icon: #224982 (Blue 700)
   * Padding: 12px × 6px | Font: 14px Medium
   */
  default: `${pillBase} bg-brand-100 text-brand-900 px-3 py-1.5 text-label-md`,

  /**
   * Emphasis Pill - For items that need to stand out more (e.g., custom criteria)
   * Background: #BADAFF (Blue 300) - noticeably darker than default
   * Text: #1F375B (Blue 950)
   * Padding: 12px × 6px | Font: 14px Medium
   */
  emphasis: `${pillBase} bg-brand-300 text-brand-900 px-3 py-1.5 text-label-md`,

  // ============================================
  // Semantic Variants (for status badges)
  // All use same height (py-1.5), font (text-label-md), and font-medium
  // Text colors are chosen for good contrast with each background
  // ============================================

  /** Success - Green background with dark green text */
  success: `${pillBase} bg-semantic-success-bg text-semantic-success-text px-3 py-1.5 text-label-md`,

  /** Warning - Amber background with dark amber text */
  warning: `${pillBase} bg-semantic-warning-bg text-semantic-warning-text px-3 py-1.5 text-label-md`,

  /** Error - Red background with dark red text */
  error: `${pillBase} bg-semantic-error-bg text-semantic-error px-3 py-1.5 text-label-md`,

  /** Info - Blue background, same text as default pill for consistency */
  info: `${pillBase} bg-semantic-info-bg text-brand-900 px-3 py-1.5 text-label-md`,

  /** Processing - Purple background with dark purple text */
  processing: `${pillBase} bg-semantic-processing-bg text-semantic-processing-text px-3 py-1.5 text-label-md`,
} as const;

/**
 * Icon sizes for use with buttons.
 * Icons should inherit text color.
 */
export const buttonIconSizes = {
  /** Standard icon size for buttons: 20px × 20px */
  default: "h-5 w-5",
  /** Small icon size: 16px × 16px */
  sm: "h-4 w-4",
  /** Large icon size: 24px × 24px */
  lg: "h-6 w-6",
} as const;

/**
 * Pill icon sizes and colors.
 */
export const pillIconSizes = {
  /** Standard icon size for pills: 22px × 22px */
  default: "h-[22px] w-[22px]",
  /** Icon color for primary pills */
  primaryColor: "text-brand-700",
} as const;

/** Type for button variant keys */
export type ButtonVariant = keyof typeof buttonVariants;

/** Type for pill variant keys */
export type PillVariant = keyof typeof pillVariants;
